import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { FileText, Truck as TruckFast, Shield, Clock } from 'lucide-react';
import ContactForm from '../components/forms/ContactForm';
import { allCatalogCategories } from '../constants/catalogCategories';
import CategoryCard from '../components/catalog/CategoryCard';

const CatalogPage: React.FC = () => {
  const { category } = useParams<{ category?: string }>();
  const selectedCategory = allCatalogCategories.find(c => c.id === category);

  const getPageTitle = () => {
    if (category && selectedCategory) {
      return `${selectedCategory.title} - Каталог электрооборудования | Проминнова`;
    }
    return 'Каталог электрооборудования - КТП, трансформаторы, щиты, кабели | Проминнова';
  };

  const getPageDescription = () => {
    if (category && selectedCategory) {
      return `${selectedCategory.title} от компании Проминнова. ${selectedCategory.description}. Производство, поставка, монтаж. Доставка по РФ и СНГ.`;
    }
    return 'Комплексные поставки электрооборудования: КТП, трансформаторы ТСЛ и ТСЗ, щитовое оборудование, КСО, кабели. Производство и монтаж под ключ.';
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getPageDescription()} />
      </Helmet>

      <div className="bg-primary-50 py-12">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {category && selectedCategory ? selectedCategory.title : 'Каталог электрооборудования'}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            {category && selectedCategory 
              ? selectedCategory.description 
              : 'Комплексные решения для энергоснабжения: от трансформаторов и КТП до кабельной продукции и блочно-модульных зданий'}
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        {!category && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {allCatalogCategories.map(cat => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>
        )}

        {category === 'turnkey' && (
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
              <div>
                <h2 className="text-2xl font-bold mb-6">Комплексные решения под ключ</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Предлагаем полный спектр оборудования и услуг для реализации энергетических проектов любой сложности:
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <Shield className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                    <div>
                      <h3 className="font-medium mb-1">Электрооборудование</h3>
                      <p className="text-gray-600">КТП, трансформаторы, щиты, КСО, кабели</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FileText className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                    <div>
                      <h3 className="font-medium mb-1">Электроустановочные изделия</h3>
                      <p className="text-gray-600">Розетки, выключатели, распределительные коробки</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <TruckFast className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                    <div>
                      <h3 className="font-medium mb-1">Блочно-модульные здания</h3>
                      <p className="text-gray-600">Для подстанций, офисов, складов, жилых модулей</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-primary-600 mr-3" />
                    <span>Срок реализации от 30 дней</span>
                  </div>
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-primary-600 mr-3" />
                    <span>Гарантия до 5 лет</span>
                  </div>
                  <div className="flex items-center">
                    <TruckFast className="w-5 h-5 text-primary-600 mr-3" />
                    <span>Доставка по всей России</span>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold mb-6">Получить расчет проекта</h3>
                <ContactForm />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Электрооборудование</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    КТП различной мощности
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Трансформаторы ТСЛ и ТСЗ
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Щитовое оборудование
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Камеры КСО
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Силовые кабели
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Электроустановочные изделия</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Розетки и выключатели
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Распределительные коробки
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Шинопроводы
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Кабельные каналы
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Автоматика
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Блочно-модульные здания</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Здания для КТП
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Офисные модули
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Складские помещения
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Жилые модули
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                    Специализированные здания
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="bg-primary-50 rounded-xl p-8 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold mb-4">Преимущества работы с нами</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <Shield className="w-8 h-8 text-primary-600 mr-4" />
                  <div>
                    <h3 className="font-semibold mb-2">Комплексные поставки</h3>
                    <p className="text-gray-600">
                      От трансформаторов до электроустановочных изделий – всё необходимое оборудование от одного поставщика
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FileText className="w-8 h-8 text-primary-600 mr-4" />
                  <div>
                    <h3 className="font-semibold mb-2">Производство по ГОСТ</h3>
                    <p className="text-gray-600">
                      Всё оборудование соответствует требованиям ГОСТ и имеет необходимые сертификаты
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <TruckFast className="w-8 h-8 text-primary-600 mr-4" />
                  <div>
                    <h3 className="font-semibold mb-2">Доставка по России</h3>
                    <p className="text-gray-600">
                      Организуем доставку в любой регион России и СНГ собственным транспортом или через надежных партнеров
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-6">Нужна консультация?</h3>
                <ContactForm compact={true} buttonText="Получить консультацию" />
              </div>
            </div>
          </div>
        </div>

        <div className="prose prose-lg max-w-none">
          <h2>Комплексные поставки электрооборудования</h2>
          <p>
            Компания «Проминнова» предлагает полный спектр электротехнического оборудования для промышленных предприятий, объектов энергетики и инфраструктуры. Мы обеспечиваем комплексный подход к реализации проектов любой сложности:
          </p>
          <ul>
            <li>Производство и поставка трансформаторов ТСЛ и ТСЗ мощностью от 25 до 4000 кВА</li>
            <li>Комплектные трансформаторные подстанции (КТП, КТПН, БКТП) различных конфигураций</li>
            <li>Щитовое оборудование: ВРУ, ЩР, ЩУ, шкафы управления</li>
            <li>Камеры КСО для распределительных устройств 6-10 кВ</li>
            <li>Силовые и контрольные кабели различных марок и сечений</li>
            <li>Электроустановочные изделия и комплектующие</li>
            <li>Блочно-модульные здания для размещения электрооборудования</li>
          </ul>
          <p>
            Наши специалисты помогут подобрать оптимальное решение для вашего проекта, обеспечат техническую поддержку и сопровождение на всех этапах реализации. Мы гарантируем качество оборудования, соблюдение сроков поставки и конкурентные цены.
          </p>
        </div>
      </div>
    </>
  );
};

export default CatalogPage;